import { type ComponentProps } from "react";

import { cn } from "@/shadcn/lib/utils";

import { ReactComponent as WarningCircleInfo } from "atoms/icons/warning_circle_info.svg";

import { Tooltip } from "../Tooltip/Tooltip";

export type InfoTooltipProps = Pick<ComponentProps<typeof Tooltip>, "content" | "placement"> & {
  /**
   * Accessible name for the tooltip trigger. Tooltip wraps the trigger in a focusable
   * `div role="button"`, so without a text node inside, screen readers announce an unlabeled
   * button — the label renders visually hidden next to the icon.
   */
  readonly triggerLabel?: string;
  /**
   * Extra classes for the floating panel. Mainly a z-index escape hatch: the default `z-120` sits
   * under a Dialog panel (`z-121`), so a tooltip opened inside a modal needs lifting (`z-150!`).
   */
  readonly className?: string;
};

export const InfoTooltip = ({ content, placement, triggerLabel, className }: InfoTooltipProps) => {
  if (!content) return null;
  return (
    <Tooltip
      className={cn("z-120 max-w-52", className)}
      content={content}
      placement={placement}
      trigger={
        // `relative` is load-bearing: `sr-only` positions the label absolutely, and without a
        // positioned ancestor here it resolves against the nearest one — inside a modal, the
        // dialog surface itself. The label then sits outside the body's scroll container and
        // stretches the surface's scrollable area by the whole scrolled-away height, painting a
        // tall empty band under the footer.
        <span className="relative flex items-center">
          <WarningCircleInfo className="h-4 w-4" />
          {triggerLabel ? <span className="sr-only">{triggerLabel}</span> : null}
        </span>
      }
    />
  );
};
